import {
  ReactNode, useEffect, useMemo, useState,
} from 'react';
import { Navigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { AuthContext } from '../../../utils/auth';
import LoadingModal from '../../loadingModal';
import { User } from '../../../types/core.type';
import UserAdapter from '../../../services/adapters/user';
import { isSessionExpiredRedirectQueued } from '../../../services/adapters/baseAdapter';

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();

  const values = useMemo(() => ({ user, setUser }), [user]);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        const userAdapter = new UserAdapter();
        const userData = await userAdapter.adaptResponse();
        setUser(userData);

        if (userData && posthog) {
          posthog.identify(userData.email, {
            email: userData.email,
            role: userData.role,
            organisation: userData.organisationDisplayName,
          });
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    void initializeUser();
  }, [setUser, posthog]);

  if (loading) {
    return <LoadingModal message="...loading" />;
  }

  if (!loading && user === null) {
    return isSessionExpiredRedirectQueued() ? null : <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
  );
}
